import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionGuard } from '@/components/layout';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePositionExport, usePositionsList } from '@/hooks/organization';
import { normalizeNumericSelectValue } from '@/utils';
import { SECTIONS } from '@/types/sections';


export const PositionsTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
  } = usePositionsList();
  const { exportPositions, isExporting } = usePositionExport({
    searchText: undefined,
    status: filters.status,
    areaId: filters.areaId,
  });

  return (
    <div className="flex flex-col gap-6" data-testid="positions-tab">
      <div className="flex justify-between items-start">
        <PermissionGuard
          action="create"
          fallback="hidden"
          denied={!canSection(SECTIONS.CARGOS, 'create')}
        >
          <PrimaryButton
            iconLeft={<IconScout name="plus" />}
            onClick={openCreatePanel}
          >
            {translate('organization:actions.createPosition')}
          </PrimaryButton>
        </PermissionGuard>

        <ExportDropdown
          onExport={exportPositions}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <FilterDropdown
          filters={filterConfig}
          values={{
            status: filters.status,
            areaId: filters.areaId ? String(filters.areaId) : 'all',
          }}
          onChangeFilter={(key, value) => {
            const selectedValue = Array.isArray(value) ? value[0] : value;

            setFilters((previous) => ({
              ...previous,
              [key]: key === 'areaId'
                ? (selectedValue === 'all'
                  ? undefined
                  : normalizeNumericSelectValue(selectedValue) ?? undefined)
                : value,
            }));
          }}
        />
      </div>

      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        searchable
        onSearch={onSearch}
        columnMaxWidth={180}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        data-testid="positions-table"
        isLoading={loading}
        actions={actions}
      />
    </div>
  );
};

export default PositionsTab;
